import React from "react";
import { SectionContentProps } from "../../types";
import EditableText from "../../core/EditableText";
import {
    PiPlus,
    PiTrash,
    PiLightning,
    PiChartBar,
    PiGlobe,
    PiStar,
    PiShieldCheck,
    PiGear
} from "react-icons/pi";
import { HokProducts, HokBlog } from "../../hok";
import OvanPortfolio from "../../ovan/ovanportfolio/OvanPortfolio";

const ICONS: Record<string, React.ElementType> = {
    lightning: PiLightning,
    chart: PiChartBar,
    globe: PiGlobe,
    star: PiStar,
    shield: PiShieldCheck,
    gear: PiGear,
};

const ICON_KEYS = Object.keys(ICONS);

export default function ServicesSection({ content, onContentChange, isSelected }: SectionContentProps) {
    const variant = content?.variant;

    if (variant === "ovan") {
        return <OvanPortfolio content={content} onContentChange={onContentChange} isSelected={isSelected} />;
    }

    if (variant === "hok") {
        return <HokProducts content={content} onContentChange={onContentChange} isSelected={isSelected} />;
    }

    if (variant === "hok-blog") {
        return <HokBlog content={content} onContentChange={onContentChange} isSelected={isSelected} />;
    }

    const primaryColor = content.colorPalette?.primary || "#3B82F6";
    const fontFamily = content.fontFamily || "'Inter', sans-serif";

    // Ensure default content structure
    const services = content.services || [
        { title: "Strategic Planning", description: "We help you define your vision and roadmap for success.", icon: "chart" },
        { title: "Global Reach", description: "Expand your business across borders with our network.", icon: "globe" },
        { title: "Fast Execution", description: "Rapid deployment of solutions to keep you ahead.", icon: "lightning" },
    ];

    const addService = () => {
        const newItems = [...services, {
            title: "New Service",
            description: "Description of the new service.",
            icon: "star"
        }];
        onContentChange?.({ ...content, services: newItems });
    };

    const removeService = (idx: number) => {
        const newItems = services.filter((_: any, i: number) => i !== idx);
        onContentChange?.({ ...content, services: newItems });
    };

    const updateService = (idx: number, field: string, value: any) => {
        const newItems = [...services];
        newItems[idx] = { ...newItems[idx], [field]: value };
        onContentChange?.({ ...content, services: newItems });
    };

    const cycleIcon = (idx: number) => {
        const currentIcon = services[idx].icon || "star";
        const currentIndex = ICON_KEYS.indexOf(currentIcon);
        const nextIndex = (currentIndex + 1) % ICON_KEYS.length;
        updateService(idx, "icon", ICON_KEYS[nextIndex]);
    };

    const renderIcon = (iconName: string, className: string) => {
        const Icon = ICONS[iconName] || PiStar;
        return <Icon className={className} />;
    };

    return (
        <div
            className={`bg-gray-50 px-8 py-20 ${isSelected ? "ring-2 ring-blue-500" : ""}`}
            style={{ fontFamily }}
        >
            <div className="mx-auto max-w-6xl">
                <div className="text-center mb-16">
                    <h2
                        className="text-4xl font-extrabold tracking-tight mb-4"
                        style={{ color: primaryColor }}
                    >
                        <EditableText
                            value={content.title || "Our Services"}
                            onChange={(val) => onContentChange?.({ ...content, title: val })}
                            as="span"
                            placeholder="Section Title"
                        />
                    </h2>
                    <p className="max-w-2xl mx-auto text-xl text-gray-500">
                        <EditableText
                            value={content.subtitle || "Comprehensive solutions tailored to your business needs."}
                            onChange={(val) => onContentChange?.({ ...content, subtitle: val })}
                            as="span"
                            placeholder="Add a subtitle for this section..."
                        />
                    </p>
                </div>

                <div className="grid gap-8 md:grid-cols-3">
                    {services.map((service: any, idx: number) => (
                        <div
                            key={idx}
                            className="group relative rounded-2xl bg-white p-8 shadow-sm hover:shadow-xl transition-all duration-300 border border-gray-100"
                        >
                            {isSelected && (
                                <button
                                    onClick={(e) => { e.stopPropagation(); removeService(idx); }}
                                    className="absolute top-4 right-4 hidden group-hover:flex h-8 w-8 items-center justify-center rounded-full bg-red-50 text-red-500 hover:bg-red-100 transition-colors"
                                    title="Remove Service"
                                >
                                    <PiTrash className="h-4 w-4" />
                                </button>
                            )}

                            <div
                                className="mb-6 inline-flex p-3 rounded-lg cursor-pointer transition-transform hover:scale-110 active:scale-95"
                                style={{ backgroundColor: `${primaryColor}10`, color: primaryColor }}
                                onClick={() => isSelected && cycleIcon(idx)}
                                title={isSelected ? "Click to change icon" : ""}
                            >
                                {renderIcon(service.icon, "h-8 w-8")}
                            </div>

                            <h3 className="mb-3 text-xl font-bold text-gray-900">
                                <EditableText
                                    value={service.title}
                                    onChange={(val) => updateService(idx, "title", val)}
                                    as="span"
                                    placeholder="Service Title"
                                />
                            </h3>

                            <p className="text-gray-600 leading-relaxed">
                                <EditableText
                                    value={service.description}
                                    onChange={(val) => updateService(idx, "description", val)}
                                    as="span"
                                    placeholder="Service description..."
                                />
                            </p>
                        </div>
                    ))}

                    {isSelected && (
                        <button
                            onClick={addService}
                            className="flex flex-col items-center justify-center rounded-2xl border-2 border-dashed border-gray-300 bg-gray-50/50 p-8 text-gray-400 hover:border-blue-400 hover:bg-blue-50 hover:text-blue-500 transition-all min-h-[300px]"
                        >
                            <div className="rounded-full bg-white p-3 shadow-sm mb-3">
                                <PiPlus className="h-6 w-6" />
                            </div>
                            <span className="font-semibold">Add New Service</span>
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
}
